import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Clock, X, Check, Settings } from 'lucide-react';
import { useUserTimezone } from '@/hooks/useUserTimezone';
import { getTimezoneDisplayWithOffset } from '@/utils/timezone-detection';
import { useAuth } from '@/contexts/AuthContext';
import { detectUserTimezone } from '@/utils/timezone-detection';

const getTimezoneOffsetMinutes = (tz: string): number => {
  try {
    const now = new Date();
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      timeZoneName: 'shortOffset',
    });
    const parts = formatter.formatToParts(now);
    const offsetPart = parts.find(p => p.type === 'timeZoneName');
    if (!offsetPart) return 0;
    // Parse "GMT+8", "GMT-5:30", "GMT" etc.
    const match = offsetPart.value.match(/GMT([+-]?)(\d+)?(?::(\d+))?/);
    if (!match) return 0;
    const sign = match[1] === '-' ? -1 : 1;
    const hours = parseInt(match[2] || '0', 10);
    const minutes = parseInt(match[3] || '0', 10);
    return sign * (hours * 60 + minutes);
  } catch {
    return 0;
  }
};

const TimezoneConfirmationBanner: React.FC = () => {
  const { user } = useAuth();
  const {
    timezone,
    isConfirmed,
    isLoading,
    source,
    userType,
    confirmTimezone,
  } = useUserTimezone();
  const navigate = useNavigate();
  const [dismissed, setDismissed] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [mismatchDetected, setMismatchDetected] = useState(false);

  const dismissKey = user?.id ? `timezone-banner-dismissed-${user.id}` : null;

  // Check sessionStorage for prior dismissal
  useEffect(() => {
    if (dismissKey && sessionStorage.getItem(dismissKey) === 'true') {
      setDismissed(true);
    }
  }, [dismissKey]);

  // Compare stored timezone with browser timezone
  useEffect(() => {
    if (isLoading || !timezone || source !== 'profile') {
      // If no profile timezone, no mismatch to detect
      setMismatchDetected(false);
      return;
    }

    const browserTz = detectUserTimezone();
    const storedOffset = getTimezoneOffsetMinutes(timezone);
    const browserOffset = getTimezoneOffsetMinutes(browserTz);
    const diffMinutes = Math.abs(storedOffset - browserOffset);

    // Only show if difference >= 60 minutes
    setMismatchDetected(diffMinutes >= 60);
  }, [timezone, isLoading, source]);

  if (isLoading || dismissed) return null;

  // Only show when there's an actual timezone mismatch between stored and browser
  if (!mismatchDetected) return null;

  // Already confirmed and no mismatch won't reach here due to guard above

  const profilePath = userType === 'practitioner'
    ? '/practitioner-dashboard?tab=profile'
    : '/client-profile';

  const handleDismiss = () => {
    setDismissed(true);
    if (dismissKey) {
      sessionStorage.setItem(dismissKey, 'true');
    }
  };

  const browserTz = detectUserTimezone();

  return (
    <Alert className="border-amber-300 bg-amber-50 mb-4">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 flex-1">
          <Clock className="h-4 w-4 text-amber-600 shrink-0" />
          <AlertDescription className="text-amber-800 text-sm">
            Your current location timezone appears to be <strong>{getTimezoneDisplayWithOffset(browserTz)}</strong>,
            but your profile is set to <strong>{getTimezoneDisplayWithOffset(timezone!)}</strong>.
            Would you like to update it?
          </AlertDescription>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Button
            size="sm"
            variant="outline"
            className="border-amber-400 text-amber-700 hover:bg-amber-100"
            onClick={() => navigate(profilePath)}
          >
            <Settings className="h-3 w-3 mr-1" />
            Update
          </Button>
          <Button
            size="icon"
            variant="ghost"
            className="h-6 w-6 text-amber-500 hover:text-amber-700"
            onClick={handleDismiss} aria-label="Close">
            <X className="h-3 w-3" />
          </Button>
        </div>
      </div>
    </Alert>
  );
};

export default TimezoneConfirmationBanner;
